import type { MockCache, StorageSettings } from '../../types';

import { bump, requireCache } from '../runtime';

export const STORAGE_SETTINGS_KEY = 'storage-settings';
export const STORAGE_ROW_ID = 'storage';
const MAX_LIMIT_BYTES = 2000 * 1024 * 1024;
const REGION_PATTERN = /^[a-z0-9-]{1,64}$/;
const BUCKET_PATTERN = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
const KEY_ID_PATTERN = /^[\x21-\x7e]{1,128}$/;

export type StorageSettingsPatch = Partial<Omit<StorageSettings, 'id' | 'updatedAt' | 'updatedBy'>>;

type PatchKey = keyof StorageSettingsPatch;

const PATCH_KEYS: readonly PatchKey[] = [
  'endpoint',
  'publicEndpoint',
  'region',
  'bucket',
  'accessKeyId',
  'pathStyle',
  'maxAttachmentBytes',
];

export function selectStorageSettings(cache: MockCache): StorageSettings | null {
  return cache.storageSettings.get(STORAGE_ROW_ID) ?? null;
}

function emptyRow(): StorageSettings {
  return {
    id: STORAGE_ROW_ID,
    endpoint: null,
    publicEndpoint: null,
    region: null,
    bucket: null,
    accessKeyId: null,
    pathStyle: null,
    maxAttachmentBytes: null,
    updatedAt: null,
    updatedBy: null,
  };
}

function isPlainAddress(value: string): boolean {
  if (/[?#\s]/.test(value)) return false;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.pathname === '/' && url.username === '';
  } catch {
    return false;
  }
}

function requireAddress(value: string, label: string): string {
  const address = value.trim().replace(/\/$/, '');
  if (!isPlainAddress(address)) {
    throw new Error(`${label}: нужен адрес http:// или https:// без пути, параметров и фрагмента`);
  }
  return address;
}

function requireMatch(value: string, pattern: RegExp, message: string): string {
  if (!pattern.test(value)) throw new Error(message);
  return value;
}

function checkedValue(key: PatchKey, value: NonNullable<StorageSettingsPatch[PatchKey]>): string | boolean | number {
  switch (key) {
    case 'endpoint':
      return requireAddress(String(value), 'Адрес хранилища');
    case 'publicEndpoint':
      return requireAddress(String(value), 'Адрес хранилища для браузера');
    case 'region':
      return requireMatch(String(value), REGION_PATTERN, 'Регион: от 1 до 64 знаков, строчные латинские буквы, цифры и дефис');
    case 'bucket':
      return requireMatch(
        String(value),
        BUCKET_PATTERN,
        'Бакет: от 3 до 63 знаков, строчные латинские буквы, цифры, точка и дефис, начало и конец — буква или цифра',
      );
    case 'accessKeyId':
      return requireMatch(String(value), KEY_ID_PATTERN, 'Идентификатор ключа: от 1 до 128 видимых знаков без пробелов');
    case 'pathStyle':
      if (typeof value !== 'boolean') throw new Error('Адресация: нужно значение да или нет');
      return value;
    case 'maxAttachmentBytes':
      if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > MAX_LIMIT_BYTES) {
        throw new Error('Лимит файла: целое число байт от 1 до 2000 МБ');
      }
      return value;
  }
}

function requireOwnStorageFields(next: StorageSettings): void {
  if (next.endpoint !== null) return;
  if (next.bucket !== null) throw new Error('Бакет можно задать только вместе с адресом хранилища');
  if (next.publicEndpoint !== null) throw new Error('Адрес для браузера можно задать только вместе с адресом хранилища');
  if (next.pathStyle === false) throw new Error('Адресацию в стиле поддомена можно задать только вместе с адресом хранилища');
}

function requireAdmin(cache: MockCache): void {
  if (cache.users.get(cache.me)?.role !== 'admin') {
    throw new Error('Настройки хранилища меняет только администратор');
  }
}

export function updateStorageSettings(patch: StorageSettingsPatch): void {
  const cache = requireCache();
  requireAdmin(cache);
  const next: StorageSettings = { ...(selectStorageSettings(cache) ?? emptyRow()) };
  const writable = next as unknown as Record<PatchKey, string | boolean | number | null>;
  for (const key of PATCH_KEYS) {
    const value = patch[key];
    if (value !== undefined) {
      writable[key] = value === null ? null : checkedValue(key, value);
    }
  }
  requireOwnStorageFields(next);
  next.updatedAt = cache.nowMs();
  next.updatedBy = cache.me;
  cache.storageSettings.set(STORAGE_ROW_ID, next);
  bump(STORAGE_SETTINGS_KEY);
}
